import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatISO } from 'date-fns';

import { TextField, TextareaField } from '@dopomogai/ui';
import { KPIObjectiveEditor } from './KPIObjectiveEditor';
import type { Sprint, Goal } from '@dopomogai/types';

interface SprintEditorProps {
  /** The sprint data to display and edit in the form. */
  sprintData: Sprint;
  /** All available goals to choose from for KPIs. */
  availableGoals: Goal[];
  /** Callback to notify the parent of any data changes. */
  onDataChange: (data: Partial<Omit<Sprint, 'id' | 'project_id'>>) => void;
}

/**
 * A pure form component for editing the core fields of a Sprint.
 * It is designed to be used inside a modal or any other container component.
 */
export const SprintEditor: React.FC<SprintEditorProps> = ({
  sprintData,
  availableGoals,
  onDataChange,
}) => {
  const { t } = useTranslation('admin_sprints');

  const formatDateForInput = (dateString?: string | null) => {
    if (!dateString) return '';
    // HTML date input needs 'yyyy-MM-dd' format
    return formatISO(new Date(dateString), { representation: 'date' });
  };

  const handleDateChange = (field: 'start_date' | 'end_date', dateValue: string) => {
    if (!dateValue) {
      onDataChange({ [field]: null });
      return;
    }
    // Convert 'yyyy-MM-dd' back to an ISO string with time for consistency
    const date = new Date(dateValue);
    onDataChange({ [field]: date.toISOString() });
  };

  return (
    <div className="space-y-6 p-1">
      <TextField
        name="sprint-name"
        label={t('editor.nameLabel')}
        value={sprintData.name}
        onChange={(e) => onDataChange({ name: e.target.value })}
        placeholder={t('editor.namePlaceholder')}
        required
      />
      <TextareaField
        name="sprint-goal"
        label={t('editor.goalLabel')}
        value={sprintData.goal || ''}
        onChange={(e) => onDataChange({ goal: e.target.value })}
        placeholder={t('editor.goalPlaceholder')}
        rows={2}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          name="start-date"
          label={t('editor.startDateLabel')}
          type="date"
          value={formatDateForInput(sprintData.start_date)}
          onChange={(e) => handleDateChange('start_date', e.target.value)}
        />
        <TextField
          name="end-date"
          label={t('editor.endDateLabel')}
          type="date"
          value={formatDateForInput(sprintData.end_date)}
          onChange={(e) => handleDateChange('end_date', e.target.value)}
        />
      </div>

      <KPIObjectiveEditor
        objectives={sprintData.kpi_objectives || []}
        availableGoals={availableGoals}
        onObjectivesChange={(newObjectives) => onDataChange({ kpi_objectives: newObjectives })}
      />
    </div>
  );
};